import React, { cloneElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperProps, Menu, Box, PopoverOrigin } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import IconButton from '../IconButton';
import { usePopover } from '../../../submodules/web-shared/hooks';

export interface ActionButtonComponentProps {
  disabled?: boolean;
  isMenuOpen?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  loading?: boolean;
}

interface ActionMenuProps {
  disabled?: boolean;
  children?: ((props: { onClose: () => void }) => React.ReactNode) | React.ReactNode;
  tooltipText?: string;
  ActionButtonComponent?: React.ReactNode;
  horizontalIcon?: boolean;
  loading?: boolean;
  transformOrigin?: PopoverOrigin;
}

const defaultTransformOrigin = {
  vertical: 'top',
  horizontal: 'right',
} as PopoverOrigin;

const ActionMenu = ({
  disabled,
  children,
  tooltipText,
  ActionButtonComponent,
  horizontalIcon,
  loading,
  transformOrigin = defaultTransformOrigin
}: ActionMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, onOpen, onClose] = usePopover();
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);

  const prevent = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    onOpen(e);
    e.stopPropagation();
    actionButtonRef.current = e.currentTarget as HTMLButtonElement;
  };

  const defaultActionBtn = <IconButton
    title={tooltipText ?? t('moreActions')}
    testID='action-menu-btn'
    disabled={disabled}
    onClick={onClick}>
    {horizontalIcon ? <MoreHorizIcon /> : <MoreVertIcon />}
  </IconButton>;

  const renderActionButton = () => {
    if (ActionButtonComponent) {
      return cloneElement(ActionButtonComponent as React.ReactElement, {
        disabled,
        onClick,
        isMenuOpen: !!anchorEl,
      });
    }
    return defaultActionBtn;
  };

  return (
    <>
      {renderActionButton()}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={(e: React.MouseEvent<HTMLElement>) => {
          prevent(e);
          !loading && onClose();
        }}
        slotProps={{
          list: {
            disablePadding: true,
            style: { minWidth: actionButtonRef?.current?.offsetWidth },
          },
          paper: {
            'data-test-id': 'action-menu-popup',
          } as PaperProps
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={transformOrigin}
      >
        <Box onKeyDown={e => e.stopPropagation()}>
          {typeof children === 'function' ? children({ onClose }) : children}
        </Box>
      </Menu>
    </>
  );
};
export default ActionMenu;
